import React, { useEffect, useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";

// One place that owns <title>, description, canonical, robots and social tags
// for every URL. Before this, only the home view set a title, so /about,
// /contact, /privacy... all showed the same title in Google.

export const SITE_URL = "https://noryq.in";

// TODO: replace with a 1200x630 /social-preview.png once you have one
// (the old tag pointed at a file that doesn't exist in /public).
const SOCIAL_IMAGE = `${SITE_URL}/logo512.png`;

const HOME = {
  title: "NORYQ – Movie & TV Show Recommendations Based on Your Mood",
  description:
    "Tell NORYQ how you feel and get AI-powered movie and TV show picks that match your mood. No endless scrolling – find what to watch tonight.",
};

// robots: "index" pages appear in Google; "noindex" pages are kept out.
export const ROUTES = {
  "/": { ...HOME, canonical: "/" },
  // /home shows the same page as "/", so it points Google at "/".
  "/home": { ...HOME, canonical: "/" },
  "/explore": {
    title: "Explore Movies & TV Shows | NORYQ",
    description:
      "Spin the NORYQ poster gallery and discover movies and TV shows you might not find on a streaming home screen.",
    canonical: "/explore",
  },
  "/about": {
    title: "About NORYQ | Why We Built a Mood-Based Watch Finder",
    description:
      "Learn who is behind NORYQ and why we built a faster way to choose what to watch, based on your mood instead of endless scrolling.",
    canonical: "/about",
  },
  "/whyus": {
    title: "Why NORYQ? Stop Scrolling, Start Watching",
    description:
      "See how NORYQ helps you pick a movie or series in minutes: mood-based questions, smart picks from your watch history, and where to stream each title.",
    canonical: "/whyus",
  },
  "/contact": {
    title: "Contact NORYQ | Support, Feedback & Grievances",
    description:
      "Get in touch with the NORYQ team for support, feedback, partnerships or privacy and grievance requests.",
    canonical: "/contact",
  },
  "/privacy": {
    title: "Privacy Policy | NORYQ",
    description:
      "How NORYQ collects, uses and protects your personal data, and the choices and rights you have over it.",
    canonical: "/privacy",
  },
  "/terms": {
    title: "Terms of Service | NORYQ",
    description:
      "The terms that apply when you use NORYQ, including accounts, acceptable use and Premium plans.",
    canonical: "/terms",
  },
  "/cookies": {
    title: "Cookie Policy | NORYQ",
    description:
      "Which cookies and similar storage NORYQ uses, why we use them, and how to change your choices at any time.",
    canonical: "/cookies",
  },
  "/refund": {
    title: "Refund & Cancellation Policy | NORYQ",
    description:
      "How refunds and cancellations work for NORYQ Premium purchases.",
    canonical: "/refund",
  },

  // Signed-in pages: useful to people, useless in search results.
  "/history": { title: "Watch history | NORYQ", noindex: true },
  "/my-list": { title: "My list | NORYQ", noindex: true },
  "/liked": { title: "Liked movies & shows | NORYQ", noindex: true },
  "/admin": { title: "Admin | NORYQ", noindex: true },
};

// Any URL we don't recognise still renders the app (existing behaviour),
// so tell Google not to index it as a duplicate of the home page.
const UNKNOWN = { ...HOME, noindex: true };

export const normalizePath = (p) =>
  (p || "/").replace(/\/+$/, "").toLowerCase() || "/";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "NORYQ",
      description: HOME.description,
      inLanguage: "en",
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "NORYQ",
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo512.png`,
    },
  ],
};

export default function SEO({ path }) {
  const key = normalizePath(path);
  const meta = ROUTES[key] || UNKNOWN;
  const description = meta.description || HOME.description;
  const canonicalUrl = meta.canonical ? `${SITE_URL}${meta.canonical}` : null;

  // index.html ships default tags for crawlers that don't run JavaScript
  // (WhatsApp, Facebook...). Drop them once React takes over so the page
  // never has two descriptions or two canonicals.
  useLayoutEffect(() => {
    document
      .querySelectorAll("[data-default-seo]")
      .forEach((node) => node.remove());
  }, []);

  // Several pages (About, Privacy, Terms...) still set document.title
  // themselves on mount. Re-assert ours just after, so the title always
  // matches the table above. (Those one-liners can be deleted over time.)
  useEffect(() => {
    const t = setTimeout(() => {
      document.title = meta.title;
    }, 50);
    return () => clearTimeout(t);
  }, [meta.title]);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={
          meta.noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large"
        }
      />
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="NORYQ" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={description} />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:image" content={SOCIAL_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SOCIAL_IMAGE} />

      {key === "/" ? (
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      ) : null}
    </Helmet>
  );
}
